import { convertGeometryToOpenLayers } from "../../geom"
import { RegularShapeFeature } from "../../map/regularShapeEditInteraction"
import { getDefaultMapMarkerStyles } from "../../map/styles/markers"
import { getTextStyles } from "../../map/styles/text"
import { IMapTopologyService } from "../../map/topo/types"
import { IMapTextOptions } from "../../map/types"
import { isFeatureLine, isFeaturePoint } from "../../map/utils"
import { LocaleString } from "../../types"
import { calculateAngle } from "../../utils/geom"
import { isObject } from "../../utils/utils"
import { clipSegmentToBoundary, clipToBoundary } from "../clip"
import { DatalayerZone } from "../datalayerZone"
import { DefaultLineWidth, IDataLayerZone, IGenericFeatureStyle, IMapFeatureStyle, LinePattern, ZoneTextLocation } from "../types"
import { DrawingLayer } from "./layer"
import { MapRegularShapeType, LayerZoneTypeId as ZoneType } from "../../generated/enums"
import { ol_Feature, ol_format, ol_proj, ol_style, ol_geom } from "@omnia/exports/openlayers";

export class DrawingFeature extends DatalayerZone {

    protected _shape: RegularShapeFeature;

    protected _style: IGenericFeatureStyle = {};

    private _usePictureCoordinates: boolean;

    protected _fieldId: number

    protected _isBuffer: boolean
    protected _buffer: number
    protected _bufferLine: ol_geom.LineString

    protected _labelPosition: ol_geom.Geometry


    constructor(_layer: DrawingLayer, _zone: IDataLayerZone, _feature?: ol_Feature) {

        super(_layer, _zone, _feature);

        const style = _zone.Style || {}

        this._style = {

            fillColor: style.Fill?.Color,

            showStroke: style.Stroke ? style.Stroke.Width > 0 : true,

            lineWidth: style.LineWidth || DefaultLineWidth,
            linePattern: style.LinePattern || LinePattern.Solid,

            autoLabel: style.AutoLabel,

        }

        this._fieldId = _zone.FieldId

        this._buffer = _zone.Buffer
        this._bufferLine = _zone.BufferLine ? _layer.getGeometryFromWKT<ol_geom.LineString>(_zone.BufferLine) : null
        this._isBuffer = !!(this._buffer || this._bufferLine)

        if (_zone.LabelStyle && _zone.LabelStyle.Position) {
            this._labelPosition = _layer.getGeometryFromWKT(_zone.LabelStyle.Position)
        }

        this.setUsePictureCoordinates()

    }

    get usePictureCoordinates(): boolean {
        return this._usePictureCoordinates;
    }

    set usePictureCoordinates(value: boolean) {
        this._usePictureCoordinates = value;
        if (value) {
            this.setFeatureToPictureCoordinates();
        }
    }

    setUsePictureCoordinates() {

        if (this.picture && this.picture.ExifLocation) {

            var pictureCoordinates = this.getPictureCoordinates();
            var pointCoordinates = this.getFeatureCoordinates();

            if (pictureCoordinates &&
                pointCoordinates &&
                pictureCoordinates[0] === pointCoordinates[0] &&
                pictureCoordinates[1] === pointCoordinates[1]) {
                this._usePictureCoordinates = true;
                return;
            }

        }

        this._usePictureCoordinates = false;

    }

    getFeatureCoordinates(): number[] {

        const geom = this.feature
            ? this.feature.getGeometry()
            : convertGeometryToOpenLayers(this.zone.Geometry)

        if (geom) {

            return (<any>geom).getCoordinates();

        }

    }

    getPictureCoordinates(): number[] {

        if (this.picture && this.picture.ExifLocation) {

            var format = new ol_format.WKT();
            var oGeom = this.picture.ExifLocation.Geometry;
            var geom = format.readGeometry(oGeom.WellKnownText, {
                dataProjection: "ESPG:4326"
            });

            if (geom) {

                var c = (<any>geom).getCoordinates();
                return ol_proj.transform(c, 'EPSG:4326', 'EPSG:3857');

            }

        }

    }

    setFeatureToPictureCoordinates() {

        var coordinates = this.getPictureCoordinates();

        if (coordinates) {

            (<any>this.feature.getGeometry()).setCoordinates(coordinates);
        }

    }

    hasDimension() {
        if (this.isText()) return false
        return this.isLine() || this.isPolygon() || this.isCircle()
    }

    isPolygon() {
        return !(this.isPoint() || this.isMarker() || this.isText() || this.isLine())
    }

    isLine() {
        return isFeatureLine(this.feature)
    }

    isPoint() {
        return isFeaturePoint(this.feature)
    }

    isMarker() {
        return this.isPoint()
    }

    isText() {
        return (this.zoneType == ZoneType.Text)
    }

    isFieldFeature(fieldId?: number) {
        return (this.zoneType == ZoneType.Field) && (!!this._fieldId) && ((!fieldId) || (this._fieldId === fieldId))
    }

    isBuffer() {
        return this._isBuffer
    }

    get bufferLine() {
        return this._bufferLine
    }

    get buffer() {
        return this._buffer
    }

    set buffer(value: number) {
        this._buffer = value
    }

    get fieldId() {
        return this._fieldId
    }

    getCurrentWKT(): string {
        return this._layer.getGeometryAsWKT(this.feature.getGeometry());
    }

    get fillColor(): number[] {
        return this._style.fillColor;
    }

    set fillColor(value: number[]) {
        this.setFillColor(value, true)
    }

    public setFillColor(value: number[], refresh: boolean) {
        this._style.fillColor = value
        if (refresh) this.refresh()
    }

    get showStroke() {
        return this._style.showStroke
    }

    set showStroke(value) {
        this._style.showStroke = value
        this.refresh()
    }

    isTransparent() {
        return !(this.fillColor || this.showStroke || this.label)
    }

    get lineWidth() {
        return this._style.lineWidth
    }

    set lineWidth(value) {
        this._style.lineWidth = value
        this.refresh()
    }

    get linePattern() {
        return this._style.linePattern
    }

    set linePattern(value) {
        this._style.linePattern = value
        this.refresh()
    }

    get autoLabel() {
        return this._style.autoLabel
    }

    set autoLabel(value: boolean) {
        this._style.autoLabel = value
        this.refresh()
    }

    getDescription(full: boolean = true): string {

        let d = this.label || ("New feature" as LocaleString)

        if (full && this.area) {

            d += " - " + Math.round(this.area / 100) / 100 + " ha";

        }

        return d;
    }

    getText(): { text: string }[] {

        const text = (this.getZoneText(ZoneTextLocation.MapFeature) || "").trim()

        const texts = text
            ? [{ text: text, fontSize: 14 }]
            : []

        return texts

    }

    isRegularShape() {
        return !!this._shape;
    }

    isCircle() {
        return this._shape && (this._shape.type == MapRegularShapeType.Circle)
    }

    get shape(): RegularShapeFeature {
        return this._shape;
    }

    setShape(shape: RegularShapeFeature) {
        this._shape = shape;
        this.setFeature(shape.feature);
    }

    getUpdateModel() {

        let model = super.getUpdateModel();

        model.FieldId = this._fieldId

        if (this._shape) {
            model.RegularShape = this._shape.shapeDefinition.asSerializable()
        }

        const style: IMapFeatureStyle = {}

        if (this.fillColor) {
            style.Fill = { Color: this.fillColor }
        }

        if (!this.showStroke) {
            style.Stroke = { Width: 0 }
        }

        if (this.lineWidth && (this.lineWidth != DefaultLineWidth)) {
            style.LineWidth = this.lineWidth
        }

        if (this.linePattern && (this.linePattern != LinePattern.Solid)) {
            style.LinePattern = this.linePattern
        }

        if (this.autoLabel) {
            style.AutoLabel = true
        }

        if (Object.keys(style).length) {
            model.Style = style
        }

        if (this.isBuffer() && this.bufferLine) {
            model.Buffer = this.buffer
            model.BufferLine = this.layer.getGeometryAsWKT(this.bufferLine);
        }

        return model;

    }

    getZoneFillColor() {
        return this.fillColor;
    }

    getZonePointStyle(selected: boolean, resolution): ol_style.Style[] {

        if (this.isMarker()) {

            const geom = this.feature ? this.feature.getGeometry() : undefined

            const styles = getDefaultMapMarkerStyles({
                color: this.fillColor,
                geometry: f => geom ? geom : f.getGeometry(),
                selected,
                scale: 1
            })

            return styles

        }

    }

    canClipToBoundary() {
        return this.feature && this.feature.getGeometry() && (!this.isBuffer())
    }

    clipToBoundary(topo: IMapTopologyService, boundary: ol_geom.Geometry) {

        if (this.isPoint() && this.usePictureCoordinates) {
            return
        }

        if (this.isText()) {
            clipSegmentToBoundary(this.feature, boundary)
            return
        }

        return clipToBoundary(this.feature, boundary, topo)

    }

    getTextStyles(resolution: number, offsetY: number) {

        if (this.isPoint()) {
            offsetY = 3
        }

        const backgroundFillColor = [0, 0, 0, 0.5]
        const backgroundFill = backgroundFillColor ? new ol_style.Fill({ color: backgroundFillColor }) : undefined

        const options: IMapTextOptions = {
            offsetY,
            backgroundFill,
            rotation: this.getTextRotationAngle(),
            textBaseline: this.isMarker() ? "top" : null,
            line: this._labelPosition instanceof ol_geom.LineString ? this._labelPosition : null
        }

        const text = this.getText()

        const styles = getTextStyles(resolution, text, options);

        return styles

    }

    getZoneText(location: ZoneTextLocation = ZoneTextLocation.MapFeature) {

        let text = super.getZoneText(location) || ""

        if (this.autoLabel) {

            const { length, area } = this.getCurrentDimensions()

            if (this.isBuffer() && length && area) {
                text = (text + "\n" + this.formatArea(area) + " - " + this.formatLength(length)).trim()
            }
            else if (this.isLine() && length) {
                text = (text + "\n" + this.formatLength(length)).trim()
            }
            else if (area) {
                text = (text + "\n" + this.formatArea(area)).trim()
            }

        }

        if ((!text) && this.isText()) {
            text = "(Default text)" as LocaleString
        }

        return text

    }

    private formatLength(length: number) {
        return length ? Math.round(length) + " m" : ""
    }

    private formatArea(area: number) {
        return area ? Math.round(area / 100) / 100 + " ha" : ""
    }

    private getTextRotationAngle() {

        const geom = this._labelPosition || (this.isText() && this.feature ? this.feature.getGeometry() : null)

        if (geom instanceof ol_geom.LineString) {
            const coords = geom.getCoordinates()
            if (coords.length > 1) {
                const center = coords[0]
                const p1 = coords[1]
                const p2 = [p1[0], center[1]]
                return calculateAngle(center, p1, p2)
            }
        }

    }

    getCurrentDimensions() {

        if (!isObject(this._dimension)) {

            if (!this.feature) {
                return {
                    area: null,
                    length: null
                }
            }

            let dim = this.measure(this.feature)

            if (this.isBuffer() && this.bufferLine) {
                // 210416 - Usersnap 2126 - Stop accounting for the extra (2 * buffer)
                // const length = this.measureLength(this.bufferLine) + (2 * (this.buffer || 0))
                const length = this.measureLength(this.bufferLine)
                dim = { ...dim, length }
            }

            this._dimension = dim

        }

        return { ...this._dimension }

    }

}
